import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {PagedModel} from '@shared/api/model/paged-model';
import {Collector, CollectorNote, CollectorType, Maybe, Site} from '@app/graphql-typings/graphql';
import * as gql from 'gql-query-builder';
import {GRAPHQL_TYPES, HttpUtilityService} from '@shared/services/api/http-utility-service';
import {map} from 'rxjs/operators';
import {ClassTransformer, Exclude, Expose, Transform, Type} from 'class-transformer';
import moment, {Moment} from 'moment-timezone';
import {BacerConfig} from '@app/layout/sitemanager/bacers/bacer-config/bacer.types';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {PageInfo} from '@shared/components/ace-table/ace-table.component';
import {HttpClient} from '@angular/common/http';
import {BaseService} from '@shared/services/api/base-service';
import Fields from 'gql-query-builder/build/Fields';
import {CollectionUtils} from '@shared/util/collections/collection-utils';
import {FavouriteType} from "@app/layout/sitemanager/collectors-favourites/favourites.types";

export class CollectorSearchTerm {
  collector_id?: number;
  collector_ids?: number;
  site_id?: number;
  collector_type?: CollectorType;
  available?: boolean;
}

export class CollectorScopeOptions {
  fetchLatestNote?: boolean;
  getSiteTimeZone?: boolean;
}

export class CollectorNoteImpl implements CollectorNote {
  collector_note_id: string;
  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  created_at: Moment;
  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  last_modified_at: Moment;
  note_text: string;
  user_id: string;
}

export interface CollectorScanHistory {
  scan_id: string;
  collector_id: number;
  start_ts: Moment;
  end_ts: Moment;
  favourite_count: number;
  data_count: number;
  missing_favourites: number[];
  reason: string;
}

class CollectorScanHistoryImpl implements CollectorScanHistory {
  scan_id: string;
  collector_id: number;
  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  start_ts: Moment;
  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  end_ts: Moment;
  get scan_duration_seconds(): number | null{
    return this.end_ts && this.start_ts ? this.end_ts.diff(this.start_ts, 'seconds') : null;
  }
  favourite_count: number;
  data_count: number;
  missing_favourites: number[];
  reason: string;
}

export class CollectorImpl implements Collector {
  site_id?: number;
  bacnet_broadcast?: string | null /* BACNet broadcast address. */;
  bacnet_scan_range?: string | null /* BACNet scan range. */;
  broadcast?:
    | string
    | null /* Primary physical network interface Broadcast address. */;
  broadcast_2?:
    | string
    | null /* Secondary physical network interface Broadcast address. */;
  collection_interval?:
    | string
    | null /* How often Collector gathers data and sends to API. */;
  collector_id: number /* Unique identifier for a Collector. */;
  description?: string | null /* Description of Collector. */;
  dhcp?: boolean | null /* Primary physical network uses DHCP? */;
  dhcp_2?: boolean | null /* Secondary physical network uses DHCP? */;
  dns?: string | null /* Primary physical network DNS server(s). */;
  dns_2?: string | null /* Secondary physical network DNS server(s). */;
  gateway?: string | null /* Primary physical network interface Gateway. */;
  gateway_2?: string | null /* Secondary physical network interface Gateway. */;
  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.parseZone(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  history_synced_at: Moment /* Timestamp of last successful sync with API. */;
  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.parseZone(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  last_polled_at: Moment /* Timestamp of last successful poll with API. */;
  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.parseZone(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  last_modified_at: Moment /* Timestamp of last successful poll with API. */;
  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.parseZone(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  registered_at: Moment /* Timestamp of Collector creation/registration. */;
  hostname?: string | null /* Hostname of Collector. */;
  interface_name?: string | null /* Primary physical network interface. */;
  interface_name_2?: string | null /* Secondary physical network interface. */;
  ip_address?:
    | string
    | null /* Primary physical network interface IP address. */;
  ip_address_2?:
    | string
    | null /* Secondary physical network interface IP address. */;
  is_active: boolean /* Is Collector active? */;
  mac_address?:
    | string
    | null /* Primary physical network interface MAC address. */;
  mac_address_2?:
    | string
    | null /* Secondary physical network interface MAC address. */;

  @Exclude({toPlainOnly: true})
  site: Site /* Site object for current Collector. */;
  subnet_mask?:
    | string
    | null /* Primary physical network interface Subnet mask. */;
  subnet_mask_2?:
    | string
    | null /* Secondary physical network interface Subnet mask. */;
  sync_interval?:
    | number
    | null /* How often Collector syncs with API (in seconds). */;
  collector_type: CollectorType;
  // TODO cleanup: once BE starts to expose configuration as :JSON
  @Type(() => CollectorNoteImpl)
  collector_notes?: Maybe<CollectorNote[]>;
  @Exclude({toPlainOnly: true})
  _configuration: BacerConfig;

  @Expose({toPlainOnly: true})
  get configuration(): BacerConfig {
    return this._configuration;
  }

  set configuration(configuration: BacerConfig) {
    this._configuration = configuration;
  }
}

@Injectable({
  providedIn: 'root'
})
export class CollectorsService extends BaseService {
  public static readonly modelMapping = {Collector: CollectorImpl, CollectorScanHistory: CollectorScanHistoryImpl};

  private static readonly collectorFields = ['collector_id', 'collector_type', '_configuration', 'hostname',
    'description', 'ip_address', 'mac_address', 'virtual', 'site_id', 'description', 'interface_name',
    'broadcast', 'ip_address', 'is_active', 'mac_address_2', 'history_synced_at', 'last_polled_at', 'dhcp', 'interface_name_2',
    'subnet_mask_2', 'dhcp_2', 'gateway', 'gateway_2', 'broadcast_2', 'mac_address', 'sync_interval',
    'dns', 'bacnet_scan_range', 'dns_2', 'collection_interval', 'subnet_mask', 'ip_address_2', 'bacnet_broadcast'];

  private static readonly latestCollectorNoteQuery = {
    operation: 'collector_notes',
    variables: {
      latest: {type: GRAPHQL_TYPES.BOOLEAN, value: true, required: true}
    },
    fields: [
      'collector_note_id',
      'note_text',
      'created_at',
      'last_modified_at',
      'user_id'
    ]
  };

  private readonly COLLECTOR_PAGE_LIMIT = 300;
  private readonly logger: AceLogger;
  private readonly DATA_KEY = 'collectors';
  private readonly DATA_KEY_PLURAL = this.DATA_KEY;
  private readonly DEFAULT_COLLECTOR_SCOPE_OPTIONS: CollectorScopeOptions = {fetchLatestNote: false, getSiteTimeZone: false};

  constructor(private httpUtilityService: HttpUtilityService,
              private classTransformer: ClassTransformer,
              private http: HttpClient,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(CollectorsService.name);

  }

  getAllCollectors(searchTerm: CollectorSearchTerm,
                   scopeOptions: CollectorScopeOptions = this.DEFAULT_COLLECTOR_SCOPE_OPTIONS): Observable<Collector[]> {
    this.logger.debug('getAvailableCollectors searchTerm', searchTerm);
    const getCollectorsWrapper = (pageInfo: PageInfo): Observable<PagedModel<Collector[]>> => {
      return this.getCollectors(pageInfo, searchTerm, scopeOptions);
    };
    return this.httpUtilityService
      .pageUntilComplete(getCollectorsWrapper, {first: 0, rows: this.COLLECTOR_PAGE_LIMIT});
  }

  getCollectors(pageInfo: PageInfo,
                searchTerm: CollectorSearchTerm,
                scopeOptions: CollectorScopeOptions = this.DEFAULT_COLLECTOR_SCOPE_OPTIONS): Observable<PagedModel<Collector[]>> {
    let fields: Fields = CollectorsService.collectorFields;

    if (scopeOptions?.getSiteTimeZone) {
      fields = [...fields, {site:[ 'timezone']} ];
    }

    if (scopeOptions?.fetchLatestNote) {
      fields = [...fields, CollectorsService.latestCollectorNoteQuery];
    }

    const query = gql.query({
      operation: 'collectors',
      fields: fields,
      variables: {
        collector_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
        site_id: {type: GRAPHQL_TYPES.INTEGER},
        available: {type: GRAPHQL_TYPES.BOOLEAN},
        collector_type: {type: 'CollectorTypeEnum'},
        collector_id: {type: GRAPHQL_TYPES.INTEGER},
        start_index: {type: GRAPHQL_TYPES.INTEGER, required: true},
        limit: {type: GRAPHQL_TYPES.INTEGER, required: true}
      }
    });

    return this.httpUtilityService.graphQL<any[]>(query, this.DATA_KEY_PLURAL,
      Object.assign({start_index: pageInfo.first, limit: pageInfo.rows}, searchTerm))
      .pipe(map(result => {
        const modelType = CollectorsService.modelMapping['Collector'];
        return {
          data: this.classTransformer.plainToInstance(modelType, result.data),
          count: result.count
        };
      }));
  }

  getCollector(collectorId: number): Observable<Collector | null> {
    const pageInfo: PageInfo = {first: 0, rows: 1};
    const searchTerm: CollectorSearchTerm = {collector_id: collectorId};
    return this.getCollectors(pageInfo, searchTerm)
      .pipe(map((value) => CollectionUtils.only(value.data)));
  }

  createVacer(siteId: number): Observable<Collector> {
    const collectorType: CollectorType = CollectorType.Virtual;
    const options = {headers: this.getHeaders()};
    return this.http.post(this.getGateway() + '/collectors', {
      site_id: siteId,
      collector_type: collectorType
    }, options).pipe(
      map(response => {
        if (response?.['status'] === 'success') {
          return response['data']['collector'];
        } else {
          this.logger.error('createVacer', response);
          return null;
        }
      }));
  }

  public calculateNonBacnetFavouriteType(bacerConfig: BacerConfig): FavouriteType | null {
    if (bacerConfig.haystack?.['enabled?']) {
      return FavouriteType.HAYSTACK;
    } else if (bacerConfig.sqlserver?.['enabled?']) {
      return FavouriteType.SQLSERVER;
    } else if (bacerConfig.smartconnector?.['enabled?']) {
      return FavouriteType.SMARTCONNECTOR;
    } else if (bacerConfig.siemens?.['enabled?']) {
      return FavouriteType.SIEMENS;
    }
    return null;
  }

  public calculateDefaultCommissionFavouriteType(collector: Collector): FavouriteType | null {
    if (collector.collector_type === CollectorType.Virtual) {
      return FavouriteType.VIRTUAL;
    } else {
      const bacerConfig = collector.configuration;
      if (bacerConfig) {
        return this.calculateNonBacnetFavouriteType(bacerConfig) ?? FavouriteType.BACNET;
      }
    }
    return null;
  }
}
